<template>
    <div>
        <v-card tile v-if="survey">
            <div class="card-title">{{ $t('Survey') }}</div>
            <v-card-text>
                <v-form ref="surveyForm" v-model="valid" lazy-validation>
                    <v-card tile class="my-4 elevation-0">
                        <v-card-text>
                            <v-row>
                                <v-col cols="3">
                                    <v-text-field dense outlined v-model.lazy="survey.nameAr" :rules="nameRules" :label="$t('NameAr')" @change="handleCopyValue('survey.nameAr', 'survey.nameEn')" />
                                </v-col>
                                <v-col cols="3" style="display:none">
                                    <v-text-field dense outlined v-model.lazy="survey.nameEn" :label="$t('NameEn')" />
                                </v-col>
                                <v-col cols="3">
                                    <DatePicker dense outlined v-model.lazy="survey.startDate" :label="$t('StartDate')" :rules="nameRules" />
                                </v-col>
                                <v-col cols="3">
                                    <DatePicker dense outlined v-model.lazy="survey.expiryDate" :label="$t('ExpiryDate')" :rules="nameRules" />
                                </v-col>
                                <v-col cols="3">
                                    <StructureSelect :outlined="outlined" dense v-model.lazy="survey.structureId" :label="$t('Structure')" :rules="nameRules" :disabled="true" />
                                </v-col>
                                <v-col sm="5" md="5" cols="12">
                                    <v-autocomplete searchable
                                                    multiple
                                                    chips
                                                    clearable
                                                    dense
                                                    outlined
                                                    v-model.lazy="selectedMunicipalityItems"
                                                    :items="itemsMunicipality"
                                                    item-value="id"
                                                    item-text="municipalityName"
                                                    :menu-props="{ maxHeight: '150' }"
                                                    style="z-index: 999999"
                                                    :label="$t('sharedWithMunicipality')">
                                        <template v-slot:prepend-item>
                                            <v-list-item @click="selectAllItems">
                                                <v-list-item-content>{{ $t('SelectAll') }}</v-list-item-content>
                                            </v-list-item>
                                        </template>
                                        <template v-slot:item="{ item }">
                                            <v-list-item-content>
                                                <h4>{{ item.municipalityName }}</h4>
                                            </v-list-item-content>
                                        </template>
                                        <template v-slot:selection="{ item }">
                                            <v-chip :key="item.id" close @click:close="removeItem(item.id)">
                                                {{ item.municipalityName }}
                                            </v-chip>
                                        </template>
                                    </v-autocomplete>
                                </v-col>
                                <v-col cols="2" style="display:none">
                                    <v-switch v-model.lazy="survey.isPublished" class="ms-4 mt-0" :label="$t('PublishSurvey')" color="teal" />
                                </v-col>
                                <v-col cols="2">
                                    <v-switch v-model.lazy="survey.showOnHomePage" class="ms-4 mt-0" :label="$t('ShowOnHomePage')" color="teal" />
                                </v-col>
                                <v-col cols="2">
                                    <v-switch v-model.lazy="survey.gisIntegration" class="ms-4 mt-0" :label="$t('GisIntegration')" color="teal" />
                                </v-col>
                                <v-col cols="4" v-if="survey.gisIntegration">
                                    <v-text-field 
                                        v-model="gisIntegrationTitle" 
                                        outlined 
                                        dense 
                                        :label="$t('GisIntegrationTitle')"
                                        @input="updateGisIntegrationTitles"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-textarea v-model.lazy="survey.description" outlined rows="3" :label="$t('Description')" />
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>

                    <v-row>
                        <v-col cols="12">
                            <v-card v-for="(page, pageIndex) in survey.pages" :key="'p' + pageIndex" tile class="mt-4 elevation-5" color="grey lighten-3">
                                <v-toolbar dense color="primary" class="text-center white--text">
                                    <v-spacer></v-spacer>
                                    <h2> {{ $t('Page')}}  {{ pageIndex +1 }}</h2>
                                    <v-spacer></v-spacer>
                                </v-toolbar>
                                <v-card-text>
                                    <v-row class="mt-3">
                                        <v-col cols="6">
                                            <v-text-field dense outlined v-model.lazy="page.nameAr" :rules="nameRules" :label="$t('NameAr')" @change="handleCopyValue(`survey.pages.${pageIndex}.nameAr`, `survey.pages.${pageIndex}.nameEn`)" />
                                        </v-col>
                                        <v-col cols="6" style="display:none">
                                            <v-text-field dense outlined v-model.lazy="page.nameEn" :label="$t('NameEn')" />
                                        </v-col>
                                    </v-row>

                                    <Draggable :list="page.questions" class="p-4" @end="updateItemOrder(page)">
                                        <transition-group dense>
                                            <v-expansion-panels v-for="(question, index) in page.questions" :key="'q' + index">
                                                <v-expansion-panel tile class="mt-4 elevation-0 drag" color="grey lighten-5" style="border: 3px solid var(--v-secondary-base) !important">
                                                    <v-expansion-panel-header background-color="#fefefe">
                                                        <b>{{ question.questionAr }}</b>

                                                    </v-expansion-panel-header>
                                                    <v-expansion-panel-content>
                                                        <v-card>
                                                            <div class="text-center drag">
                                                                <v-icon size="35" color="primary">mdi-drag-horizontal</v-icon>
                                                                <span style="float:left">
                                                                    <v-tooltip bottom v-if="question.questionTypeId">
                                                                        <template v-slot:activator="{ on, attrs }">
                                                                            <v-btn small fab class="ma-3" v-bind="attrs" v-on="on" @click="selectedQuestionTypeId = question.questionTypeId; previewDialog = true ">
                                                                                <v-icon color="primary">
                                                                                    mdi-eye
                                                                                </v-icon>
                                                                            </v-btn>
                                                                        </template>
                                                                        <span>{{ $t('Preview') }}</span>
                                                                    </v-tooltip>

                                                                    <v-btn small fab v-if="!disabled" @click="removeQuestion(page, index)" class="ma-5">
                                                                        <v-icon color="error">
                                                                            mdi-trash-can-outline
                                                                        </v-icon>
                                                                    </v-btn>
                                                                </span>
                                                            </div>

                                                            <v-card-text class="pt-0" style="clear:both">
                                                                <v-row>
                                                                    <v-col cols="12" md="6" class="py-0">
                                                                        <v-text-field dense outlined v-model.lazy="question.questionAr" append-icon="mdi-comment-question-outline" :rules="nameRules" :label="$t('QuestionAr')" :disabled="survey.isPublished" @change="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.questionAr`, `survey.pages.${pageIndex}.questions.${index}.questionEn`)" />
                                                                    </v-col>
                                                                    <v-col cols="12" md="6" class="py-0">
                                                                        <v-text-field dense outlined v-model.lazy="question.noteAr" append-icon="mdi-comment-outline" :label="$t('NoteAr')" :disabled="survey.isPublished" @change="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.noteAr`, `survey.pages.${pageIndex}.questions.${index}.noteEn`)" />
                                                                    </v-col>
                                                                </v-row>

                                                                <v-row>
                                                                    <v-col cols="12" md="6" class="py-0" style="display:none">
                                                                        <v-text-field dense outlined v-model.lazy="question.questionEn" append-icon="mdi-comment-question-outline" :label="$t('QuestionEn')" :disabled="survey.isPublished" />
                                                                    </v-col>
                                                                    <v-col cols="12" md="6" class="py-0">
                                                                        <v-text-field dense outlined v-model.lazy="question.noteEn" append-icon="mdi-comment-outline" :label="$t('NoteEn')" :disabled="survey.isPublished" />
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="6" md="6" class="py-0">
                                                                        <v-autocomplete searchable dense outlined v-model.lazy="question.questionTypeId" :items="questionTypes" :menu-props="{ bottom: true, offsetY: true }" :rules="nameRules" append-icon="mdi-chevron-down" :disabled="survey.isPublished" item-value="id" item-text="questionTypeNameEn" :label="$t('Select')" @change="selectedMethod = $event; question.questionTypeId = $event; changeRelatedQuestionType(page, question)">
                                                                            <template v-slot:item="{ item }">
                                                                                <v-icon class="me-2" color="primary">{{ item.iconMdi }}</v-icon>
                                                                                <v-list-item-content>
                                                                                    <h4>{{ language == 'ar' ?  item.questionTypeNameAr: item.questionTypeNameEn }}</h4>
                                                                                </v-list-item-content>
                                                                            </template>
                                                                        </v-autocomplete>
                                                                    </v-col>
                                                                    <v-col cols="6" md="6" class="py-0">
                                                                        <v-autocomplete searchable dense outlined v-model.lazy="question.resultTypeId" :items="resultTypes" :menu-props="{ bottom: true, offsetY: true }" :rules="nameRules" append-icon="mdi-chevron-down" :disabled="survey.isPublished" item-value="id" item-text="nameEn" :label="$t('SelectResultType')">
                                                                            <template v-slot:item="{ item }">
                                                                                <v-icon class="me-2" color="primary">{{ item.iconMdi }}</v-icon>
                                                                                <v-list-item-content>
                                                                                    <h4>{{ language == 'ar' ?  item.nameAr: item.nameEn }}</h4>
                                                                                </v-list-item-content>
                                                                            </template>
                                                                        </v-autocomplete>
                                                                    </v-col>
                                                                </v-row>
                                                                <v-row>
                                                                    <v-col cols="12" md="1" class="text-center py-0">
                                                                        <v-checkbox v-model.lazy="question.isRequired" :label="$t('Required')" class="ma-2 mx-4" :disabled="survey.isPublished"></v-checkbox>
                                                                    </v-col>
                                                                </v-row>


                                                            </v-card-text>
                                                            <v-card-actions>
                                                                <v-expansion-panels class="mb-4">
                                                                    <v-expansion-panel key="panel1">
                                                                        <v-expansion-panel-header background-color="#fefefe">
                                                                            <b>{{ $t('Settings') }}</b>

                                                                        </v-expansion-panel-header>
                                                                        <v-expansion-panel-content>
                                                                            <!--shortAnswer textArea-->
                                                                            <v-text-field v-if="question.questionTypeId == shortAnswer || question.questionTypeId == textArea" outlined v-model.lazy="question.default" hide-detail dense :label="$t('DefaultText')" />
                                                                            <!--TextEditor-->
                                                                            <div v-if="question.questionTypeId == textEditor" class="container">
                                                                                <TextEditor v-model="question.questionTypeParams" />
                                                                            </div>
                                                                            <!--YesNo-->
                                                                            <LikeDislike :disabled="survey.isPublished" v-if="question.questionTypeId == yesNo"></LikeDislike>

                                                                            <!--Label-->
                                                                            <div class="text-center" v-if="question.questionTypeId == label">
                                                                                <v-text-field class="mt-3" outlined v-model.lazy="question.default" hide-detail dense :label="$t('Text')" />
                                                                            </div>

                                                                            <!--UploadFiles-->
                                                                            <div class="text-center" v-if="question.questionTypeId == UploadFiles">
                                                                                <UploadArrayCheckboxList v-model="question.questionTypeParams" :divStyle="'display:inline-flex'" :checkboxStyle="'margin-left:25px;'" :options="fileUploadOptions" dense />
                                                                            </div>

                                                                            <!--Consent-->
                                                                            <div class="text-center" v-if="question.questionTypeId == Consent">
                                                                                <v-text-field outlined v-model="question.questionTypeParams" hide-detail dense />
                                                                            </div>

                                                                            <!--Ranking-->
                                                                            <div class="text-center" v-if="question.questionTypeId == Ranking">
                                                                                <Ranking outlined v-model="question.questionTypeParams" hide-detail dense />
                                                                            </div>

                                                                            <!--ContactForm-->
                                                                            <div class="text-center" v-if="question.questionTypeId == ContactForm">
                                                                                <ContactForm :validate="false" :isAdmin="false" class="mt-3" outlined />
                                                                            </div>

                                                                            <!--NPS-->
                                                                            <div class="text-center" v-if="question.questionTypeId == nps">
                                                                                <NPS :increaseDecrease="true" v-model="question.questionTypeParams"></NPS>
                                                                            </div>

                                                                            <!--Rating-->
                                                                            <div class="text-center" v-if="question.questionTypeId == rating">
                                                                                <div style="float:right;margin-top:10px">
                                                                                    <v-select v-if="question.selectedRateOption!=null" dense outlined style="width:50%" v-model="question.selectedRateOption" :items="['Star','Emoji']" @change="CheckRatingOption(question)"></v-select>
                                                                                    <v-select v-else dense outlined style="width:50%" v-model="question.questionTypeParams" :items="['Star','Emoji']" @change="CheckRatingOption(question)"></v-select>
                                                                                </div>
                                                                                <div style="float: right; width: 60%; margin-top: 10px ">
                                                                                    <v-rating v-if="question.selectedRateOption=='Star' || question.questionTypeParams ==='Star'" v-model.lazy="rated" :length="rateLength" large></v-rating>
                                                                                    <SmileyRating v-if="question.selectedRateOption=='Emoji' || question.questionTypeParams ==='Emoji'"></SmileyRating>
                                                                                </div>
                                                                            </div>

                                                                            <!--BipolarMatrix-->
                                                                            <bipolar-matrix v-if="question.questionTypeId == bipolarMatrix" :label="$t('BipolarMatrix')" v-model="question.questions"></bipolar-matrix>

                                                                            <!--Image-->
                                                                            <div class="text-center mt-3" v-if="question.questionTypeId == image" :disabled="isDisabled || (survey.isPublished && !hasUpdatePublishedPermission)">
                                                                                <v-row>
                                                                                    <v-col cols="12" sm="12" md="6">
                                                                                        <!-- File input for image selection -->
                                                                                        <input 
                                                                                            type="file"
                                                                                            accept="image/*"
                                                                                            @change="onImageFileChange($event, question)"
                                                                                            :ref="'fileInput_' + question.id"
                                                                                            style="display: none"
                                                                                        />
                                                                                        
                                                                                        <v-btn rounded class="my-2" outlined @click="$refs['fileInput_' + question.id][0].click()">
                                                                                            <v-icon size="20" class="me-3">mdi-upload</v-icon>
                                                                                            {{$t('Upload')}}
                                                                                        </v-btn>

                                                                                        <!-- Image Cropper Dialog -->
                                                                                        <v-dialog v-model="question.showCropper" max-width="900px" persistent>
                                                                                            <v-card>
                                                                                                <v-card-title class="primary white--text">
                                                                                                    <v-icon color="white" class="me-2">mdi-crop</v-icon>
                                                                                                    {{$t('CropImage')}}
                                                                                                    <v-spacer></v-spacer>
                                                                                                    <v-btn icon dark @click="closeCropper(question)">
                                                                                                        <v-icon>mdi-close</v-icon>
                                                                                                    </v-btn>
                                                                                                </v-card-title>
                                                                                                <v-card-text class="pt-4">
                                                                                                    <cropper
                                                                                                        v-if="question.cropperImage"
                                                                                                        :src="question.cropperImage"
                                                                                                        :stencil-props="{
                                                                                                            aspectRatio: NaN,
                                                                                                            movable: true,
                                                                                                            resizable: true
                                                                                                        }"
                                                                                                        :canvas="{
                                                                                                            maxWidth: 1920,
                                                                                                            maxHeight: 1920
                                                                                                        }"
                                                                                                        :ref="'cropper_' + question.id"
                                                                                                        class="cropper-wrapper"
                                                                                                    />
                                                                                                    
                                                                                                    <!-- Zoom Control -->
                                                                                                    <v-row class="mt-4 align-center">
                                                                                                        <v-col cols="12">
                                                                                                            <v-subheader class="px-0">
                                                                                                                <v-icon class="me-2">mdi-magnify</v-icon>
                                                                                                                {{$t('Zoom')}}
                                                                                                            </v-subheader>
                                                                                                            <v-slider
                                                                                                                v-model="question.cropperZoom"
                                                                                                                @input="onZoomChange(question)"
                                                                                                                :min="0"
                                                                                                                :max="2"
                                                                                                                :step="0.01"
                                                                                                                prepend-icon="mdi-magnify-minus"
                                                                                                                append-icon="mdi-magnify-plus"
                                                                                                                hide-details
                                                                                                            ></v-slider>
                                                                                                        </v-col>
                                                                                                    </v-row>

                                                                                                    <!-- Rotation Controls -->
                                                                                                    <v-row class="mt-2 align-center">
                                                                                                        <v-col cols="12">
                                                                                                            <v-subheader class="px-0">
                                                                                                                <v-icon class="me-2">mdi-rotate-right</v-icon>
                                                                                                                {{$t('Rotate')}}
                                                                                                            </v-subheader>
                                                                                                            <v-btn-toggle v-model="question.rotationAngle" mandatory class="d-flex flex-wrap">
                                                                                                                <v-btn small @click="rotateImage(question, -90)">
                                                                                                                    <v-icon>mdi-rotate-left</v-icon>
                                                                                                                    <span class="ms-1">-90°</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="rotateImage(question, 90)">
                                                                                                                    <v-icon>mdi-rotate-right</v-icon>
                                                                                                                    <span class="ms-1">+90°</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="flipImageHorizontal(question)">
                                                                                                                    <v-icon>mdi-flip-horizontal</v-icon>
                                                                                                                    <span class="ms-1">{{$t('FlipH')}}</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="flipImageVertical(question)">
                                                                                                                    <v-icon>mdi-flip-vertical</v-icon>
                                                                                                                    <span class="ms-1">{{$t('FlipV')}}</span>
                                                                                                                </v-btn>
                                                                                                                <v-btn small @click="resetCropper(question)" color="warning">
                                                                                                                    <v-icon>mdi-restore</v-icon>
                                                                                                                    <span class="ms-1">{{$t('Reset')}}</span>
                                                                                                                </v-btn>
                                                                                                            </v-btn-toggle>
                                                                                                        </v-col>
                                                                                                    </v-row>

                                                                                                    <!-- Size Info -->
                                                                                                    <v-row class="mt-2">
                                                                                                        <v-col cols="12">
                                                                                                            <v-chip small outlined class="me-2">
                                                                                                                <v-icon small class="me-1">mdi-information</v-icon>
                                                                                                                {{$t('DragToResize')}}
                                                                                                            </v-chip>
                                                                                                        </v-col>
                                                                                                    </v-row>
                                                                                                </v-card-text>
                                                                                                <v-divider></v-divider>
                                                                                                <v-card-actions class="pa-4">
                                                                                                    <v-spacer></v-spacer>
                                                                                                    <v-btn text large @click="closeCropper(question)">
                                                                                                        <v-icon class="me-1">mdi-close</v-icon>
                                                                                                        {{$t('Cancel')}}
                                                                                                    </v-btn>
                                                                                                    <v-btn color="primary" large @click="saveCroppedImage(question)">
                                                                                                        <v-icon class="me-1">mdi-check</v-icon>
                                                                                                        {{$t('Save')}}
                                                                                                    </v-btn>
                                                                                                </v-card-actions>
                                                                                            </v-card>
                                                                                        </v-dialog>

                                                                                        <v-row v-if="question.default">
                                                                                            <v-slider v-model="zoom" @change="createImage(zoom, question)"
                                                                                                      append-icon="mdi-magnify-plus-outline"
                                                                                                      prepend-icon="mdi-magnify-minus-outline"></v-slider>
                                                                                        </v-row>
                                                                                    </v-col>
                                                                                    <v-col cols="6">
                                                                                        <v-select v-model="question.imagePosition"
                                                                                                  item-text="name"
                                                                                                  item-value="value"
                                                                                                  :items="positions"
                                                                                                  :label="$t('SelectPosition')">
                                                                                        </v-select>
                                                                                    </v-col>
                                                                                </v-row>
                                                                                <v-row>
                                                                                    <v-col cols="12" sm="6" md="6">
                                                                                        <!-- Display the preview image next to the input file element -->
                                                                                        <v-img v-if="question.default" :ref="'img_'+question.id" :src="question.default" height="100%" :width="questionImgWidth(question.imageWidth)" :alt="previewMedia" class="preview-image"></v-img>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </div>

                                                                            <!--vedioLink-->
                                                                            <v-text-field class="mt-3" v-if="question.questionTypeId == vedioLink" outlined v-model.lazy="question.default" hide-detail dense :label="$t('VedioLink')" />

                                                                            <!--PhoneNumber-->
                                                                            <phoneNumber :isAdmin="true" class="mt-3" v-if="question.questionTypeId == PhoneNumber" outlined v-model="question.questionTypeParams" />

                                                                            <!--PictureChoice-->
                                                                            <PictureChoice @input="handleImageChange" @imageRemoved="handleImageRemoved" class="mt-3" v-if="question.questionTypeId == PictureChoice" outlined v-model="question.questionPictures" />

                                                                            <!--Url-->
                                                                            <div class="text-center" v-if="question.questionTypeId == Url">
                                                                                <Url v-model="question.questionTypeParams"></Url>
                                                                            </div>

                                                                            <!--EmailInput-->
                                                                            <emailInput class="mt-3" :required="false" v-if="question.questionTypeId == EmailInput" outlined v-model="question.selectedValue" />


                                                                            <!--Hijri Date-->
                                                                            <div class="text-center" v-if="question.questionTypeId == HijriDate">
                                                                                <hijriDatePicker v-model="question.questionTypeParams"></hijriDatePicker>
                                                                            </div>

                                                                            <!--PredefineDropdown-->
                                                                            <template v-if="question.questionTypeId == PredefineDropdown">
                                                                                <v-row>
                                                                                    <v-col md="3">
                                                                                        <v-select class="mt-4" :rules="nameRules" :disabled="survey.isPublished && !hasUpdatePublishedPermission" v-model="question.lookupId" dense :items="itemsForDropdown(question)" :label="$t('StartWith')" outlined item-text="name" item-value="id" @change="handleSelectionChange(question, $event)"></v-select>
                                                                                    </v-col>
                                                                                    <v-col v-if="dependentItems.length > 0" md="3">
                                                                                        <v-select class="mt-4" :rules="nameRules" v-model="question.questionTypeParams" dense :items="itemsForDropdownRelated(question)" :label="$t('StopWith')" outlined item-text="name" item-value="id"></v-select>
                                                                                    </v-col>
                                                                                </v-row>
                                                                                <v-row>

                                                                                </v-row>
                                                                            </template>

                                                                            <!--multipleChoice checkbox dropdown-->
                                                                            <template v-if="question.questionTypeId == multipleChoice || question.questionTypeId == checkbox || question.questionTypeId == dropdown || question.questionTypeId == trickyQuestion">
                                                                                <v-row>
                                                                                    <v-col cols="12" md="12" class="text-start">
                                                                                        <v-btn rounded v-if="!disabled" class="mt-2" outlined @click="addOption(question)">
                                                                                            <v-icon size="20" class="me-3">mdi-plus-circle-outline</v-icon>   {{ $t('AddOptions') }}
                                                                                        </v-btn>
                                                                                    </v-col>
                                                                                </v-row>
                                                                                <v-row v-for="(item, optIndex) in question.options" :key="optIndex">
                                                                                    <v-col md="2">
                                                                                        <v-text-field outlined v-model.lazy="item.optionAr" hide-details dense :label="$t('TextAr')" :rules="nameRules" @change="handleCopyValue(`survey.pages.${pageIndex}.questions.${index}.options.${optIndex}.optionAr`, `survey.pages.${pageIndex}.questions.${index}.options.${optIndex}.optionEn`)" />
                                                                                    </v-col>
                                                                                    <v-col md="2" style="display:none">
                                                                                        <v-text-field outlined v-model.lazy="item.optionEn" hide-details dense :label="$t('TextEn')" />
                                                                                    </v-col>
                                                                                    <v-col md="1">
                                                                                        <v-icon v-if="!disabled" class="mt-2" @click="removeOption(optIndex, question.options)">
                                                                                            mdi-close
                                                                                        </v-icon>
                                                                                    </v-col>
                                                                                </v-row>
                                                                            </template>

                                                                        </v-expansion-panel-content>
                                                                    </v-expansion-panel>
                                                                    <v-expansion-panel key="panel2" style="margin-top:10px">
                                                                        <v-expansion-panel-header background-color="#fefefe">
                                                                            <b>{{ $t('Logic') }}</b>

                                                                        </v-expansion-panel-header>
                                                                        <v-expansion-panel-content>
                                                                            <v-row style="margin-top:10px">
                                                                                <v-col md="12">
                                                                                    <h3>{{ $t('DisplayLogic') }}</h3>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row v-for="(l, lindex) in question.logic" :key="'l' + lindex">
                                                                                <v-col md="12">

                                                                                    <v-btn fab v-if="!survey.isPublished || hasUpdatePublishedPermission" small color="error" @click="removeLogic(question, lindex)" class="mb-4">
                                                                                        <v-icon size="20">
                                                                                            mdi-minus
                                                                                        </v-icon>
                                                                                    </v-btn>
                                                                                    <div style="clear:both"></div>
                                                                                    <table style="width:100%">
                                                                                        <tr>
                                                                                            <td v-if="lindex>0"><v-select class="mt-4" v-if="lindex>0" v-model="l.operand" :disabled="survey.isPublished && !hasUpdatePublishedPermission" dense :items="[{ code: 'and', text: $t('And') }, { code: 'or', text: $t('Or') }]" :label="$t('Select')" outlined item-text="text" item-value="code"></v-select></td>
                                                                                            <td style="width:10px" v-if="lindex>0"></td>
                                                                                            <td><v-select class="mt-4" v-model="l.relatedQuestionId" :disabled="survey.isPublished && !hasUpdatePublishedPermission" dense @change="setLogic(page,l)" :items="filteredQuestionsForLogic(page,question)" :label="$t('DisplayTheQuestionOnlyIf')" outlined item-text="questionAr" item-value="reference"></v-select></td>
                                                                                        </tr>
                                                                                    </table>


                                                                                </v-col>
                                                                                <v-col md="6" v-if="l.relatedQuestionId!==null">
                                                                                    <v-select class="mt-4" v-model="l.operator" :disabled="survey.isPublished && !hasUpdatePublishedPermission" dense :items="filteredOperandForLogic(page, l)" :label="$t('Select')" outlined item-text="text" item-value="code"></v-select>
                                                                                </v-col>
                                                                                <v-col md="6" v-if="l.relatedQuestionId!==null">
                                                                                    <v-text-field class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == shortAnswer || l.relatedQuestionTypeId  == textArea" outlined hide-detail dense :disabled="survey.isPublished && !hasUpdatePublishedPermission" />
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == yesNo" dense outlined :label="$t('Select')" item-text="text" item-value="code" :items="[{code:'yes',text:$t('Yes')},{code:'no',text:$t('No')}]"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == checkbox || l.relatedQuestionTypeId == multipleChoice || l.relatedQuestionTypeId == dropdown || l.relatedQuestionTypeId == bipolarMatrix || l.relatedQuestionTypeId == trickyQuestion" :disabled="survey.isPublished && !hasUpdatePublishedPermission" dense :items="filteredRelatedQuestionOptions(page, l)" :label="$t('Select')" outlined item-text="optionAr" item-value="optionAr"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == PredefineDropdown" :disabled="survey.isPublished && !hasUpdatePublishedPermission" dense :items="getpredefinedItems(page, l)" :label="$t('Select')" outlined item-text="name" item-value="id"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == nps" :disabled="survey.isPublished && !hasUpdatePublishedPermission" dense :items="filteredRelatedQuestionTypeParams(page, l)" :label="$t('Select')" outlined item-text="optionAr" item-value="optionAr"></v-select>
                                                                                    <v-select class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == rating" dense outlined :label="$t('Select')" item-text="text" item-value="code" :items="['1','2','3','4','5']"></v-select>
                                                                                    <v-text-field class="mt-4" v-model="l.value" v-if="l.relatedQuestionTypeId == number" outlined hide-detail dense :disabled="survey.isPublished && !hasUpdatePublishedPermission" />
                                                                                    <div v-if="l.relatedQuestionTypeId == date" class="mt-4"><DatePicker dense outlined v-model="l.value" :rules="question.required ? nameRules : []" :disabled="survey.isPublished && !hasUpdatePublishedPermission" /></div>
                                                                                </v-col>
                                                                            </v-row>
                                                                            <v-row>
                                                                                <v-col md="12">
                                                                                    <v-btn fab v-if="!survey.isPublished || hasUpdatePublishedPermission" small color="primary" @click="addLogic(question)" class="mb-4">
                                                                                        <v-icon size="20">
                                                                                            mdi-plus
                                                                                        </v-icon>
                                                                                    </v-btn>
                                                                                </v-col>

                                                                            </v-row>
                                                                        </v-expansion-panel-content>
                                                                    </v-expansion-panel>
                                                                </v-expansion-panels>
                                                            </v-card-actions>
                                                        </v-card>
                                                    </v-expansion-panel-content>
                                                </v-expansion-panel>
                                            </v-expansion-panels>
                                        </transition-group>
                                    </Draggable>
                                    <v-row>
                                        <v-col cols="12" class="text-center">
                                            <v-btn tile small color="#86be4a" class="mt-3" dark @click="addQuestion(page)">
                                                {{$t('AddQuestion')}}
                                                <v-icon>mdi-head-question</v-icon>
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-row class="mt-3">
                        <v-col cols="12" class="text-center">
                            <h3></h3>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn fab v-if="!disabled" small color="primary" @click="addSection" v-bind="attrs" v-on="on">
                                        <v-icon size="20">
                                            mdi-file-plus-outline
                                        </v-icon>

                                    </v-btn>
                                </template>
                                <span>{{ $t('AddPage') }}</span>
                            </v-tooltip>

                        </v-col>
                    </v-row>

                </v-form>

                <v-bottom-navigation background-color="secondary" dark shift class="mt-3">
                    <v-btn v-if="!disabled" tile @click="save" color="secondary" style="height:100%">
                        {{ $t('Save') }}
                        <v-icon>
                            mdi-floppy
                        </v-icon>
                    </v-btn>
                    <v-btn tile @click="preview" color="secondary" style="height:100%" v-if="!disabled">
                        {{ $t('Preview') }}
                        <v-icon>
                            mdi-account-eye
                        </v-icon>
                    </v-btn>
                </v-bottom-navigation>
            </v-card-text>
        </v-card>
        <ControlPreview v-model="previewDialog" v-if="previewDialog" :questionTypeId="selectedQuestionTypeId"></ControlPreview>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import SurveyService from '@/services/SurveyService.js'
    import TasksService from '@/services/TasksService.js'
    import LookupAdminService from '@/services/LookupAdminServices.js'
    import StructuresService from '@/services/StructuresService.js'
    import DatePicker from '@/components/controls/DatePicker.vue'
    import StructureSelect from '@/components/controls/StructureSelect.vue'
    import VTextField from '@/components/controls/VTextField.vue'
    import ControlPreview from '@/components/controls/ControlPreview'
    import BipolarMatrix from '@/components/controls/BipolarMatrix.vue'
    import SmileyRating from '@/components/controls/SmileyRating.vue'
    import LikeDislike from '@/components/controls/LikeDislike.vue'
    import NPS from '@/components/controls/NPS.vue'
    import hijriDatePicker from '@/components/controls/HijriParent.vue'
    import phoneNumber from '@/components/controls/phoneNumber.vue'
    import emailInput from '@/components/controls/EmailInput.vue'
    import PictureChoice from '@/components/controls/PictureChoice.vue'
    import { Cropper } from 'vue-advanced-cropper'
    import 'vue-advanced-cropper/dist/style.css'
    import UploadArrayCheckboxList from '@/components/controls/UploadArrayCheckboxList.vue'
    import Ranking from '@/components/controls/RankingItems.vue'
    import TextEditor from '@/components/controls/TextEditor.vue'
    import ContactForm from '@/pages/Admin/ContactForm.vue'
    import Url from '@/components/controls/url.vue'

    import Draggable from 'vuedraggable'
    import { FormMethodEnum } from '@/helpers/constants/enumerations.js'
    import { createGuid, copyValue } from '@/helpers/common.js'

    export default {
        components: {
            DatePicker, hijriDatePicker, phoneNumber, emailInput, PictureChoice, UploadArrayCheckboxList, StructureSelect, Draggable, ControlPreview, VTextField, BipolarMatrix, LikeDislike, SmileyRating, NPS, Ranking, Cropper, TextEditor, ContactForm, Url
        },

        props: {

            isDisabled: {
                type: Boolean,
                default: false
            },
            taskId: {
                type: Number,
                default: null
            }
        },

        watch: {
            'survey.pages': {
                handler: function (val) {
                    if (!this.isFirstLoad) {
                        this.majorChange = true
                    }
                    else {
                        this.isFirstLoad = false
                    }
                },
                deep: true
            }
        },

        data() {
            return {
                editor: null,
                isFirstLoad: false,
                majorChange: false,
                response: null,
                gisIntegrationTitle: '', // Holds the GIS Integration Title
                nameRules: [(v) => !!v || this.$t('RequiredField')],
                rateLength: 5,
                rated: 0,
                selectedQuestionTypeId: null,
                previewDialog: false,
                valid: true,
                outlined: true,
                loading: false,
                hasUpdatePublishedPermission: false,
                disabled: false,
                survey: null,
                shortAnswer: FormMethodEnum.ShortAnswer,
                textEditor: FormMethodEnum.TextEditor,
                checkbox: FormMethodEnum.Checkboxes,
                multipleChoice: FormMethodEnum.MultipleChoice,
                dropdown: FormMethodEnum.Dropdown,
                trickyQuestion: FormMethodEnum.TrickyQuestion,
                rating: FormMethodEnum.Rating,
                number: FormMethodEnum.number,
                yesNo: FormMethodEnum.YesNo,
                date: FormMethodEnum.Date,
                textArea: FormMethodEnum.TextArea,
                bipolarMatrix: FormMethodEnum.BipolarMatrix,
                PredefineDropdown: FormMethodEnum.PredefineDropdown,
                image: FormMethodEnum.Image,
                vedioLink: FormMethodEnum.VedioLink,
                label: FormMethodEnum.Label,
                nps: FormMethodEnum.NPS,
                HijriDate: FormMethodEnum.HijriDate,
                PhoneNumber: FormMethodEnum.PhoneNumber,
                EmailInput: FormMethodEnum.EmailInput,
                PictureChoice: FormMethodEnum.PictureChoice,
                UploadFiles: FormMethodEnum.UploadFiles,
                Consent: FormMethodEnum.Consent,
                Ranking: FormMethodEnum.Ranking,
                ContactForm: FormMethodEnum.ContactForm,
                Url: FormMethodEnum.Url,
                uploadMessagePlaceHolder: this.$t('Upload'),
                selectedFile: null,
                previewMedia: this.$t('Preview'),
                questionTypes: [],                
                resultTypes:[],
                stringFilters: [
                    'Contains',
                    'Does Not Contain',
                    'Starts With',
                    'Ends With',
                    'Is Answered',
                    'Is Not Answered',
                    'Equals',
                    'Not eEqual To',
                ],
                dependentItems: [],
                show: true,
                params: {
                    token: '123456798',
                    name: 'avatar'
                },
                headers: {
                    smail: '*_~'
                },
                defaultValue: 100 + '%',
                imageWidth: 100,
                imagePosition: 'start',
                zoom: 100,
                slideValue: null,
                imgDataUrl: '',
                fileUploadOptions: [{ 'id': 1, 'name': 'PDF' }, { 'id': 2, 'name': 'DOC' }, { 'id': 3, 'name': 'DOCX' }, { 'id': 4, 'name': 'PNG' }, { 'id': 5, 'name': 'JPG' }, { 'id': 6, 'name': 'JPEG' }, { 'id': 7, 'name': 'GIF' }],
                selectedMunicipalityItems: [],
                itemsMunicipality: [],
                positions: [{ name: this.$t('StartPos'), value: 'start' }, { name: this.$t('End'), value: 'end' }, { name: this.$t('Middle'), value: 'middle' }],
            }
        },

        created() {
            this.isFirstLoad = true
            this.getquestionTypes()
            this.getresultTypes()
            if (this.taskId) {
                this.loadSurvey(this.taskId)
            }
            this.loadLookups()
            this.getMunicipality()
        },
        mounted() {
            this.editor = new Editor({
                content: '',
                extensions: [StarterKit,
                    Link.configure({
                        openOnClick: false,
                        autolink: true,
                        defaultProtocol: 'https',
                    })
                ],
            })
        },

        beforeDestroy() {
            this.editor.destroy()
        },
        computed: {
            ...mapGetters('user', ['language']),

            getLanguage() {
                return this.language == null ? 'ar' : this.language

            },

            itemsForDropdown() {
                return (currentQuestion) => {
                    LookupAdminService.listSurveyLookupItems(currentQuestion.lookupId)
                        .then((res) => {
                            currentQuestion.lookupoptions = []
                            res.data.forEach((el) => {
                                currentQuestion.lookupoptions.push({ id: el.id.toString(), name: el.name })
                            })
                        })
                        .catch((error) => {
                            //this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => {
                            //this.loading = false
                        })
                    var language = this.getLanguage
                    return this.lookups.map(item => ({ id: item.id, name: language == 'ar' ? item.nameAr : item.nameEn }))
                }
            },

            itemsForDropdownRelated() {
                return (currentQuestion) => {
                    LookupAdminService.listSurveyLookupItems(currentQuestion.lookupId)
                        .then((res) => {
                            currentQuestion.lookupoptions = []
                            res.data.forEach((el) => {
                                currentQuestion.lookupoptions.push({ id: el.id.toString(), name: el.name })
                            })
                        })
                        .catch((error) => {
                            //this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                        }).finally(() => {
                            //this.loading = false
                        })
                    var language = this.getLanguage
                    return this.dependentItems.map(item => ({ id: item.id.toString(), name: language == 'ar' ? item.nameAr : item.nameEn }))
                }
            },

            itemsForAllDropDown() {
                var language = this.getLanguage
                var all = []
                this.lookups.forEach(item => {
                    all.push({ id: item.id.toString(), name: language == 'ar' ? item.nameAr : item.nameEn })
                })
                this.dependentItems.forEach(item => {
                    all.push({ id: item.id.toString(), name: language == 'ar' ? item.nameAr : item.nameEn })
                })
                return all
            },

            filteredQuestionsForLogic() {
                return (page, question) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }

                    return allqs.filter(x => (x.pageId == page.number && x.orderBy < question.orderBy) || x.pageId < page.number)

                }
            },
            filteredRelatedQuestionTypeParams() {
                return (page, logic) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    if (page && logic) {
                        var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                        var bpoptions = []
                        var number = relatedquestion.questionTypeParams.split('#')[2]
                        if (number) {
                            for (let i = 0; i <= number; i++) {
                                bpoptions.push({ optionAr: i.toString(), optionEn: i.toString() })
                            }
                        }
                        return bpoptions
                    }
                    else {
                        return []
                    }
                }
            },
            filteredRelatedQuestionOptions() {
                return (page, logic) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    if (page && logic) {
                        var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                        if (logic.relatedQuestionTypeId == this.bipolarMatrix) {
                            var bpoptions = []
                            for (let q of relatedquestion.questions) {
                                for (let op of q.options) {
                                    bpoptions.push({ optionAr: q.questionAr + ':' + op.optionAr, optionEn: q.questionEn + ':' + op.optionEn })
                                }

                            }
                            return bpoptions
                        }
                        else {
                            return relatedquestion.options
                        }
                    }
                    else {
                        return []
                    }
                }
            },
            getpredefinedItems() {
                return (page, logic) => {
                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                    return relatedquestion.lookupoptions
                }
            },
            filteredOperandForLogic() {

                return (page, logic) => {

                    var allqs = []
                    for (var i = 0; i < this.survey.pages.length; i++) {
                        var p = this.survey.pages[i]
                        for (var j = 0; j < p.questions.length; j++) {
                            var q = p.questions[j]
                            allqs.push({ ...q, pageId: p.number })
                        }
                    }
                    const contains = this.$t('Contains')
                    const notcontains = this.$t('DoesNotContain')
                    const startswith = this.$t('StartsWith')
                    const endswith = this.$t('EndsWith')
                    const isanswered = this.$t('IsAnswered')
                    const isnotanswered = this.$t('IsNotAnswered')
                    const equal = this.$t('EqualTo')
                    const notequal = this.$t('NotEqualTo')
                    const respondentselected = this.$t('RespondentSelected')
                    const respondentnotselected = this.$t('RespondentNotSelected')
                    const lessthan = this.$t('LessThan')
                    const greaterthen = this.$t('GreaterThan')
                    const not = this.$t('Not')
                    const $is = this.$t('Is')
                    var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                    if (relatedquestion) {

                        switch (relatedquestion.questionTypeId) {
                            case FormMethodEnum.ShortAnswer:
                            case FormMethodEnum.TextArea:
                                return [{ code: 'contains', text: contains }, { code: 'notcontains', text: notcontains }, { code: 'starts', text: startswith }, { code: 'ends', text: endswith }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }, { code: 'equal', text: equal }, { code: 'notequal', text: notequal }]
                                break
                            case FormMethodEnum.Checkboxes:
                            case FormMethodEnum.MultipleChoice:
                            case FormMethodEnum.Dropdown:
                            case FormMethodEnum.TrickyQuestion:
                            case FormMethodEnum.PredefineDropdown:
                                return [{ code: 'equal', text: respondentselected }, { code: 'notequal', text: respondentnotselected }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.Rating:
                            case FormMethodEnum.number:
                            case FormMethodEnum.NPS:
                                return [{ code: 'lessthan', text: lessthan }, { code: 'greaterthan', text: greaterthen }, { code: 'equal', text: equal }, { code: 'notequal', text: notequal }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.YesNo:
                                return [{ code: 'equal', text: $is }, { code: 'notequal', text: not }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.Date:
                            case FormMethodEnum.BipolarMatrix:
                                return [{ code: 'equal', text: equal }, { code: 'notequal', text: notequal }, { code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break
                            case FormMethodEnum.Image:
                            case FormMethodEnum.VedioLink:
                            case FormMethodEnum.Maps:
                                return [{ code: 'answered', text: isanswered }, { code: 'notanswered', text: isnotanswered }]
                                break



                        }
                    }
                    else
                        return []
                }
            }
        },

        methods: {
            updateGisIntegrationTitles() {
                // Update both Arabic and English titles with the same value
                this.survey.gisIntegrationTitleAr = this.gisIntegrationTitle
                this.survey.gisIntegrationTitleEn = this.gisIntegrationTitle
            },
            setLink() {
                const previousUrl = this.editor.getAttributes('link').href
                const url = window.prompt('URL', previousUrl)

                // cancelled
                if (url === null) {
                    return
                }

                // empty
                if (url === '') {
                    this.editor
                        .chain()
                        .focus()
                        .extendMarkRange('link')
                        .unsetLink()
                        .run()

                    return
                }

                // update link
                this.editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .setLink({ href: url })
                    .run()
            },
            questionImgWidth(width) {
                return width + '%'
            },
            handleImageChange(images) {
                this.question.questionPictures = images
            },
            handleImageRemoved(removedImage) {
            },
            loadDependentItemsForEdit() {
                this.survey.pages.forEach((page) => {
                    page.questions.forEach((question) => {
                        if (question.questionTypeId === this.PredefineDropdown) {
                            this.loadDependentItems(question.lookupId)
                        }
                    })
                })
            },

            preview() {
                this.loading = true
                this.updateGisIntegrationTitles()
                SurveyService.addSurvey(this.survey, this.majorChange).then((res) => {
                    this.posted = true
                    this.$router.push('/form/' + res.data.id + '?viewMode=' + true + '&fromTask=' + true)
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            addSection() {
                this.survey.pages.push({ number: this.survey.pages.length + 1, nameAr: '', nameEn: '', questions: [] })
            },

            getquestionTypes() {
                LookupAdminService.listQuestionTypeItems().then((res) => {
                    this.questionTypes = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            getresultTypes() {
                LookupAdminService.listResultTypeItems().then((res) => {
                    this.resultTypes = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            CheckRatingOption(question) {
                question.questionTypeParams = question.selectedRateOption
            },

            addLogic(question) {
                question.logic.push({
                    reference: createGuid(),
                    operand: null,
                    relatedQuestionTypeId: null,
                    relatedQuestionId: null,
                    operator: '',
                    value: ''
                })
            },

            addQuestion(page) {
                page.questions.push(
                    {
                        reference: createGuid(),
                        questionAr: '',
                        questionEn: '',
                        noteEn: '',
                        noteAr: '',
                        questionTypeId: null,
                        orderBy: page.questions.length + 1,
                        options: [],
                        default: null,
                        isRequired: false,
                        logic: [
                            {
                                relatedQuestionTypeId: null,
                                relatedQuestionId: null,
                                operator: '',
                                value: ''
                            }
                        ],
                        questions: [],
                        questionTypeParams: '',
                        optionsRating: ['Star', 'Emoji'],
                        selectedRateOption: 'Star'
                    })
            },

            updateItemOrder: function (page) {
                var items = page.questions.map(function (item, index) {
                    item.orderBy = index + 1
                })
                if (this.debounce) return
                this.debounce = setTimeout(function (items) {
                    this.debounce = false
                })
            },

            removeQuestion(page, index) {
                page.questions.splice(index, 1)
            },

            removeLogic(question, index) {
                question.logic.splice(index, 1)
            },

            addOption(question) {
                let option = { optionAr: this.inputTextAr, optionEn: this.inputTextEn, order: question.options.length + 1 }
                question.options.push(option)
                this.inputTextAr = ''
                this.inputTextEn = ''
            },

            removeOption(index, options) {
                options.splice(index, 1)
            },

            loadLookups() {
                LookupAdminService.listSurveyLookups().then((res) => {
                    this.lookups = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            predefineDropdownItemsPage(currentQuestion) {
                LookupAdminService.listSurveyLookupItems(currentQuestion.lookupId)
                    .then((res) => {
                        currentQuestion.lookupoptions = []
                        res.data.forEach((el) => {
                            currentQuestion.lookupoptions.push({ id: el.id.toString(), name: el.name })
                        })
                    })
                    .catch((error) => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
            },
            handleSelectionChange(question, selected) {
                question.lookupId = selected
                this.predefineDropdownItemsPage(question)
                this.loadDependentItems(selected)
                LookupAdminService.listSurveyLookupItems(question.lookupId)
                    .then((res) => {
                        question.lookupoptions = []
                        res.data.forEach((el) => {
                            question.lookupoptions.push({ id: el.id.toString(), name: el.name })
                        })
                    })
                    .catch((error) => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
            },

            loadDependentItems(id) {
                LookupAdminService.listSurveyLookupsByParentId(id).then((res) => {
                    this.dependentItems = res.data

                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            save() {
                if (this.validate()) {
                    this.loading = true
                    this.survey.shareWithMunicipalities = this.selectedMunicipalityItems.join(',')
                    // Ensure GIS Integration Title fields are updated before saving
                    this.updateGisIntegrationTitles()
                    SurveyService.addSurvey(this.survey, this.majorChange).then((res) => {
                        this.$root.$notification(this.$t('SurveyUpdated'), 'success')
                    }).catch(() => {
                        this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },

            validate() {
                let valid = true
                if (this.$refs.surveyForm.validate()) {

                    for (var i = 0; i < this.survey.pages.length; i++) {
                        let page = this.survey.pages[i]
                        if (page) {
                            if (page.questions.length == 0) {
                                this.$root.$notification(this.$t('AddQuestionRequired') + ` (${page.nameAr}) `, 'error')
                                valid = false
                            }
                            else {
                                for (var j = 0; j < page.questions.length; j++) {
                                    let question = page.questions[j]
                                    if (question) {

                                    }
                                }
                            }
                        }
                    }
                }
                else { valid = false }
                return valid
            },

            setLogic(page, logic) {
                var allqs = []
                for (var i = 0; i < this.survey.pages.length; i++) {
                    var p = this.survey.pages[i]
                    for (var j = 0; j < p.questions.length; j++) {
                        var q = p.questions[j]
                        allqs.push({ ...q, pageId: p.number })
                    }
                }
                var relatedquestion = allqs.filter(x => x.reference == logic.relatedQuestionId)[0]
                if (relatedquestion) {
                    logic.relatedQuestionId = relatedquestion.reference
                    logic.relatedQuestionTypeId = relatedquestion.questionTypeId
                }
                else {
                    logic.relatedQuestionId = null
                    logic.relatedQuestionTypeId = null
                }
            },
            changeRelatedQuestionType(page, question) {
                page.questions.forEach(e => {
                    e.logic.forEach(x => {
                        if (x.relatedQuestionId == question.reference) {
                            x.relatedQuestionTypeId = question.questionTypeId
                        }
                    })
                })
            },
            loadSurvey(taskId) {
                this.loading = true
                TasksService.loadSurvey(taskId).then((res) => {
                    this.survey = res.data

                    // Initialize GIS Integration Title from loaded data
                    if (res.data.gisIntegrationTitleAr || res.data.gisIntegrationTitleEn) {
                        // Use the current language version or fallback to the other
                        this.gisIntegrationTitle = this.language === 'ar' 
                            ? (res.data.gisIntegrationTitleAr || res.data.gisIntegrationTitleEn)
                            : (res.data.gisIntegrationTitleEn || res.data.gisIntegrationTitleAr)
                    }

                    const municipalityItems = res.data.shareWithMunicipalities.split(',')
                    const miArrary = []
                    for (var i = 0; i < municipalityItems.length; i++) {
                        miArrary.push(Number(municipalityItems[i]))
                    }
                    this.selectedMunicipalityItems = miArrary

                    this.loadDependentItemsForEdit()
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },

            toggleShow() {
                this.show = !this.show
            },
            onImageFileChange(event, question) {
                const file = event.target.files[0]
                if (file && file.type.startsWith('image/')) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        // Initialize cropper properties if not exist
                        if (!question.cropperImage) {
                            this.$set(question, 'cropperImage', '')
                            this.$set(question, 'showCropper', false)
                            this.$set(question, 'cropperZoom', 0)
                            this.$set(question, 'rotationAngle', 0)
                        }
                        // Set the image and show cropper
                        question.cropperImage = e.target.result
                        question.showCropper = true
                        question.cropperZoom = 0
                        question.rotationAngle = 0
                    }
                    reader.readAsDataURL(file)
                }
            },
            closeCropper(question) {
                question.showCropper = false
                question.cropperImage = null
                question.cropperZoom = 0
                question.rotationAngle = 0
                // Reset file input
                const fileInput = this.$refs['fileInput_' + question.id]
                if (fileInput && fileInput[0]) {
                    fileInput[0].value = ''
                }
            },
            saveCroppedImage(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    const { canvas } = cropperRef[0].getResult()
                    if (canvas) {
                        question.default = canvas.toDataURL('image/png', 0.9)
                        this.imgDataUrl = question.default
                        this.closeCropper(question)
                    }
                }
            },
            onZoomChange(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].zoom(question.cropperZoom)
                }
            },
            rotateImage(question, angle) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].rotate(angle)
                }
            },
            flipImageHorizontal(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].flip(true, false)
                }
            },
            flipImageVertical(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].flip(false, true)
                }
            },
            resetCropper(question) {
                const cropperRef = this.$refs['cropper_' + question.id]
                if (cropperRef && cropperRef[0]) {
                    cropperRef[0].reset()
                    question.cropperZoom = 0
                    question.rotationAngle = 0
                }
            },
            cropSuccess(question, imgDataUrl) {

                this.imgDataUrl = imgDataUrl
                question.default = imgDataUrl
            },

            cropUploadSuccess(jsonData, field) {
            },

            cropUploadFail(status, field) {
            },

            handleFileUpload(question) {
                if (question.selectedFile) {
                    const file = question.selectedFile
                    if (file) {
                        const reader = new FileReader()
                        reader.onload = () => {
                            question.default = reader.result
                        }
                        reader.readAsDataURL(file)
                    }
                }
            },

            handleCopyValue(sourcePath, targetPath) {
                copyValue(sourcePath, targetPath, this, false)
            },
            getMunicipality() {
                StructuresService.listMunicipalities().then((res) => {
                    if (res.data) {
                        this.itemsMunicipality = res.data.map(item => ({
                            id: item.id,
                            municipalityName: item.name
                        }))
                    }
                }).catch(() => {
                    this.$root.$notification(this.$t('ErrorOccurred'), 'error')
                }).finally(() => {
                    this.loading = false
                })
            },
            removeItem(id) {
                const index = this.selectedMunicipalityItems.indexOf(id)
                if (index !== -1) {
                    this.selectedMunicipalityItems.splice(index, 1)
                }
            },
            selectAllItems() {
                this.selectedMunicipalityItems = this.itemsMunicipality.map(item => item.id)
            },

            createImage(value, question) {

                this.defaultValue = value + '%'
                this.imageWidth = value
                question.imageWidth = value
                var vImgComponent = this.$refs['img_' + question.id][0]
                setTimeout(function () {
                    var div = vImgComponent.$el.querySelector('.v-image__image')
                    var style = getComputedStyle(vImgComponent.$el)
                    var imgheight = parseInt(style.height)
                    var imgwidth = parseInt(style.width)
                    var imageLoader = new Image()
                    imageLoader.height = imgheight
                    imageLoader.onload = function () {
                        var imgHeight = imgheight
                        var imgWidth = 'auto'
                        var canvas = document.createElement('canvas')
                        var ctx = canvas.getContext('2d')
                        canvas.width = imgWidth
                        canvas.height = imgHeight
                        ctx.drawImage(imageLoader, 0, 0, imgwidth, imgheight)
                    }
                    imageLoader.src = getComputedStyle(div).backgroundImage.replace(/url\((['"])?(.*?)\1\)/gi, '$2')
                }, 500)
            },
        },
    }
</script>

<style scoped>
    .v-expansion-panel-header {
        border-bottom: 2px solid green;
        background: #f7f7f7;
        height: 40px
    }

    .preview-image {
        width: 150px; /* Adjust the size as needed */
        height: 150px; /* Adjust the size as needed */
    }

    .tiptap {
        height: 100%;
        padding: 10px
    }

    .cropper-wrapper {
        height: 500px;
        background: #DDD;
    }
</style>